import React, { useState } from 'react';

interface BusinessFile {
  id: string;
  name: string;
  uploadDate: string;
  size: string;
  type: string;
}

const BusinessFiles: React.FC = () => {
  const [files, setFiles] = useState<BusinessFile[]>([]);
  const [uploading, setUploading] = useState(false);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFiles = e.target.files;
    if (uploadedFiles && uploadedFiles.length > 0) {
      setUploading(true);

      // Simulate file upload
      setTimeout(() => {
        const newFiles: BusinessFile[] = Array.from(uploadedFiles).map((file, index) => ({
          id: `file_${Date.now()}_${index}`,
          name: file.name,
          uploadDate: new Date().toLocaleDateString(),
          size: `${(file.size / 1024).toFixed(2)} KB`,
          type: file.type || 'Unknown'
        }));

        setFiles([...files, ...newFiles]);
        setUploading(false);
      }, 1000);
    }
  };

  const handleDelete = (fileId: string) => {
    setFiles(files.filter(f => f.id !== fileId));
  };

  return (
    <div style={{ padding: '32px', maxWidth: '1200px' }}>
      {/* Page Header */}
      <div style={{ marginBottom: '32px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1 style={{ fontSize: '28px', fontWeight: 700, color: '#FFFFFF', margin: '0 0 8px 0' }}>
              Business files
            </h1>
            <p style={{ fontSize: '14px', color: '#87CEEB', margin: 0 }}>
              A secure, HIPAA-compliant place to store and share files
            </p>
          </div>
        </div>
      </div>

      {/* Upload Area or File List */}
      {files.length === 0 ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '80px 32px',
          backgroundColor: '#0A2540',
          borderRadius: '8px'
        }}>
          {/* Illustration */}
          <div style={{
            fontSize: '120px',
            marginBottom: '32px',
            opacity: 0.7
          }}>
            📁
          </div>

          <h2 style={{
            fontSize: '24px',
            fontWeight: 600,
            color: '#FFFFFF',
            marginBottom: '16px',
            textAlign: 'center'
          }}>
            Safely store and share your business files
          </h2>

          <p style={{
            fontSize: '14px',
            color: '#87CEEB',
            marginBottom: '32px',
            textAlign: 'center',
            maxWidth: '600px'
          }}>
            Upload files that help you run your business, like rental agreements, professional licenses, or employment contracts. Files are secure and HIPAA compliant.
          </p>

          <label style={{
            padding: '12px 32px',
            backgroundColor: '#226F9E',
            color: '#FFFFFF',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 600,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span>☁️</span>
            Upload documents
            <input
              type="file"
              multiple
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
          </label>
        </div>
      ) : (
        <div>
          {/* Upload Button */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{
              padding: '10px 24px',
              backgroundColor: '#226F9E',
              color: '#FFFFFF',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 600,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span>☁️</span>
              Upload documents
              <input
                type="file"
                multiple
                onChange={handleFileUpload}
                style={{ display: 'none' }}
              />
            </label>
          </div>

          {/* Files Table */}
          <div style={{
            backgroundColor: '#113F67',
            border: '1px solid #6CA6C1',
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#0A2540' }}>
                  <th style={{ padding: '12px', textAlign: 'left', color: '#F7E9A0', fontSize: '12px', fontWeight: 600 }}>File Name</th>
                  <th style={{ padding: '12px', textAlign: 'left', color: '#F7E9A0', fontSize: '12px', fontWeight: 600 }}>Upload Date</th>
                  <th style={{ padding: '12px', textAlign: 'left', color: '#F7E9A0', fontSize: '12px', fontWeight: 600 }}>Size</th>
                  <th style={{ padding: '12px', textAlign: 'left', color: '#F7E9A0', fontSize: '12px', fontWeight: 600 }}>Type</th>
                  <th style={{ padding: '12px', textAlign: 'left', color: '#F7E9A0', fontSize: '12px', fontWeight: 600 }}></th>
                </tr>
              </thead>
              <tbody>
                {files.map((file, index) => (
                  <tr key={file.id} style={{ borderTop: index > 0 ? '1px solid #6CA6C1' : 'none' }}>
                    <td style={{ padding: '12px', color: '#FFFFFF', fontSize: '14px' }}>
                      📄 {file.name}
                    </td>
                    <td style={{ padding: '12px', color: '#87CEEB', fontSize: '14px' }}>{file.uploadDate}</td>
                    <td style={{ padding: '12px', color: '#87CEEB', fontSize: '14px' }}>{file.size}</td>
                    <td style={{ padding: '12px', color: '#87CEEB', fontSize: '14px' }}>{file.type}</td>
                    <td style={{ padding: '12px' }}>
                      <button
                        onClick={() => handleDelete(file.id)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: 'transparent',
                          color: '#FF6B6B',
                          border: '1px solid #FF6B6B',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Uploading Indicator */}
      {uploading && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: '#0A2540',
          border: '1px solid #6CA6C1',
          borderRadius: '8px',
          padding: '24px',
          zIndex: 1000
        }}>
          <p style={{ color: '#FFFFFF', fontSize: '14px' }}>Uploading files...</p>
        </div>
      )}
    </div>
  );
};

export default BusinessFiles;
